import { Banknote, Coins, CreditCard, Gift, Wallet, type LucideIcon } from 'lucide-react';
import { cn } from '@mlm/ui';
import type { PosPaymentMethodOption } from '../../lib/api';

/** An icon by what the method DOES, so a renamed method ("BPI") still reads right. */
function iconFor(m: PosPaymentMethodOption): LucideIcon {
  if (m.tenderKind === 'cash') return Banknote;
  if (m.tenderKind === 'exact') return CreditCard;
  if (m.currency === 'gc') return Gift;
  if (m.currency === 'coin') return Coins;
  return Wallet;
}

/**
 * "Mode of Payment": the admin-managed payment methods as a grid of icon tiles.
 * A method the cart cannot be paid with (a GC tender when a line has no GC
 * price) stays visible but disabled, with the reason under its name.
 */
export function PaymentGrid({
  methods,
  selectedCode,
  onSelect,
  isDisabled,
}: {
  methods: PosPaymentMethodOption[];
  selectedCode: string | null;
  onSelect: (code: string) => void;
  isDisabled: (m: PosPaymentMethodOption) => boolean;
}) {
  return (
    <div
      className="grid grid-cols-2 gap-3 sm:grid-cols-3"
      role="radiogroup"
      aria-label="Mode of payment"
    >
      {methods.map((m) => {
        const Icon = iconFor(m);
        const disabled = isDisabled(m);
        const active = selectedCode === m.code;
        return (
          <button
            key={m.code}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={disabled}
            title={disabled ? `Not every item in the cart has a ${m.label} price` : undefined}
            onClick={() => onSelect(m.code)}
            className={cn(
              'flex min-h-[64px] items-center gap-3 rounded-lg border-2 p-[15px] text-left text-base font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50',
              active ? 'border-primary bg-primary/5 text-primary' : 'border-border bg-card',
            )}
          >
            <Icon className="h-6 w-6 shrink-0" />
            <span className="min-w-0">
              <span className="block truncate">{m.label}</span>
              {disabled && (
                <span className="block truncate text-xs font-normal text-muted-foreground">
                  Not priced in {m.label}
                </span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}
